import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Sparkles, Loader2, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const { login, verify2FA } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = new URLSearchParams(loc.search).get("next") || "/dashboard";
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result?.requires_2fa) {
        setChallenge(result.challenge);
      } else {
        toast.success(`Welcome back, ${result.name}`);
        nav(result.role === "admin" ? "/admin" : next);
      }
    } catch (e) {
      toast.error("Invalid credentials", { description: e?.response?.data?.detail || "" });
    } finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await verify2FA(challenge, code);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : next);
    } catch (e) { toast.error("Invalid 2FA code", { description: e?.response?.data?.detail || "" }); }
    finally { setLoading(false); }
  };

  return (
    <div className="container-x py-20 md:py-28 min-h-[70vh] flex items-center" data-testid="login-page">
      <div className="grid md:grid-cols-2 gap-16 w-full items-center">
        <div className="hidden md:block">
          <div className="overline mb-4">Welcome back</div>
          <h1 className="font-serif text-5xl tracking-tight text-[#111]">Your space deserves <span className="gold-shimmer-text">consistency.</span></h1>
          <p className="mt-5 text-[#4A554E]">Sign in to manage bookings, view invoices, and lock in recurring savings.</p>
        </div>
        {!challenge ? (
          <form onSubmit={submit} className="card-luxe p-8 md:p-10 max-w-md w-full mx-auto" data-testid="login-form">
            <div className="flex items-center gap-2 mb-6">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-serif text-2xl">Sign in</h2>
            </div>
            <label className="overline">Email</label>
            <input type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})}
              className="w-full px-5 py-3.5 mt-2 mb-5 rounded-full border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none"
              data-testid="login-email" />
            <label className="overline">Password</label>
            <input type="password" required value={form.password} onChange={(e)=>setForm({...form, password:e.target.value})}
              className="w-full px-5 py-3.5 mt-2 mb-3 rounded-full border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none"
              data-testid="login-password" />
            <Link to="/forgot-password" className="text-xs text-[#0B5D3B] font-medium hover:underline block mb-7" data-testid="login-forgot">Forgot password?</Link>
            <button disabled={loading} className="btn-primary w-full disabled:opacity-60" data-testid="login-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
            </button>
            <div className="text-sm text-[#4A554E] mt-5 text-center">
              New here? <Link to="/register" className="text-[#0B5D3B] font-semibold">Create account</Link>
            </div>
          </form>
        ) : (
          <form onSubmit={verify} className="card-luxe p-8 md:p-10 max-w-md w-full mx-auto" data-testid="login-2fa-form">
            <div className="flex items-center gap-2 mb-6">
              <ShieldCheck className="w-5 h-5 text-[#0B5D3B]" />
              <h2 className="font-serif text-2xl">Two-Factor verification</h2>
            </div>
            <p className="text-sm text-[#4A554E] mb-5">Enter the 6-digit code from your authenticator app.</p>
            <input value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              placeholder="123456" inputMode="numeric" maxLength={6} autoFocus
              className="w-full px-4 py-4 mb-6 rounded-xl border border-[#EAE3D2] focus:border-[#0B5D3B] focus:outline-none text-3xl tracking-[0.4em] text-center font-mono"
              data-testid="login-2fa-code" />
            <button disabled={loading || code.length !== 6} className="btn-primary w-full disabled:opacity-60" data-testid="login-2fa-submit">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying…</> : "Verify and continue"}
            </button>
            <button type="button" onClick={() => { setChallenge(null); setCode(""); }} className="block w-full text-center mt-5 text-xs text-[#7A8A81] hover:text-[#111]" data-testid="login-2fa-cancel">
              Use a different account
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
